import { ReactElement, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AxiosResultDefaultError } from '../../../api/request';
import { AuthenticationError } from '../../../api/requests/tuitionProcess';
import { createFetchContext } from '../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../components/info/DisplayError';
import Loading from '../../../components/info/Loading';
import { useParameters } from '../../../hooks/useParameters';
import { formatOptions } from '../../../utils/selectFormat';
import { formatDefaultValuesStudentFileNoSua } from '../../../utils/tuitionProcess';
import studentFileNewFormatPreviusData from '../../AdmissionAndEnrollments/OnSiteEnrollment/TuitionProcessNoSua/steps/studentFile/utils/studentFileNewFormatPreviusData';
import { useAsyncOptionsFieldsLoader } from '../../StudentFileAsyncOptions/AsyncOptionsFieldsLoader';
import { getPostulant } from '../api/studentPersonalInfoRequests';
import { StudentFileShowResponse } from '../types';

export interface StudentFileFilterLoaderProps {
  onError?: (code: string, onUnknown?: (() => void) | undefined) => void;
  children: (props: {
    refresh: () => Promise<void>;
    postulant: any;
    defaultValues: any;
    photo: string | null;
  }) => ReactElement;
}

const { FetchProvider, FetchConsumer } = createFetchContext<
  undefined,
  StudentFileShowResponse,
  AuthenticationError | AxiosResultDefaultError
>();

export const SustainerLoaderConsumer = FetchConsumer;

export default function PostulantLoader({
  onError,
  children,
}: StudentFileFilterLoaderProps) {
  const { studentId } = useParams<{ studentId: string }>();
  const {
    countryOptions,
    communeOptions,
    regionOptions,
    institutionTypeOptions,
  } = useParameters();

  const {
    data: {
      tenanCountry,
      genders,
      areaTypes,
      healthSystems,
      maritalStatus,
      ...asyncOptions
    } = {
      data: [],
    },
  } = useAsyncOptionsFieldsLoader();

  const request = useCallback(async () => {
    return getPostulant(studentId);
  }, [studentId]);

  const isNotDynamicData = (data: StudentFileShowResponse) =>
    Object.keys(data.responses).length === 0;

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ data, loading, error, refresh }) => {
          if (error) {
            onError && onError(error.code);
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          if (loading) {
            return <Loading insideCard />;
          }

          if (!data) {
            return (
              <DisplayError
                insideCard
                textBody="Data no cargada"
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          return children({
            defaultValues: isNotDynamicData(data)
              ? formatDefaultValuesStudentFileNoSua(
                  data,
                  communeOptions,
                  regionOptions,
                  countryOptions,
                  institutionTypeOptions,
                )
              : studentFileNewFormatPreviusData(data, {
                  tenanCountry,
                  genders: formatOptions(genders ?? []),
                  areaTypes: formatOptions(areaTypes ?? []),
                  healthInstitutions: formatOptions(healthSystems ?? []),
                  maritalStatus: formatOptions(maritalStatus ?? []),
                  countries: countryOptions,
                  communes: communeOptions,
                  regions: regionOptions,
                  ...Object.fromEntries(
                    Object.entries(asyncOptions).map(([key, value]) => {
                      if (Array.isArray(value)) {
                        return [key, formatOptions(value ?? [])];
                      }
                      return [key, value ?? []];
                    }),
                  ),
                }),
            photo: data.student?.photo,
            refresh,
            ...data,
            postulant: {
              ...data.postulant,
              id: data.postulant.id,
              identification:
                data.responses?.identification ??
                data.student?.account?.defaultIdentification?.value,
            },
          });
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
